import { Component, OnInit, computed, inject, input, signal, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  CalendarDatePipe,
  CalendarEvent,
  CalendarMonthViewComponent,
  CalendarNextViewDirective,
  CalendarPreviousViewDirective,
  CalendarTodayDirective,
  CalendarView,
  DAYS_OF_WEEK,
} from 'angular-calendar';
import dayjs from 'dayjs';
import { Subject } from 'rxjs';
import { ApiService } from 'src/app/services/api.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { NgClass, DatePipe, DecimalPipe } from '@angular/common';
import { MatTooltip } from '@angular/material/tooltip';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { MatIcon } from '@angular/material/icon';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  MatTable,
  MatTableDataSource,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { User } from 'src/app/models/user.model';
import { EmployeeCalendarRegister } from 'src/app/models/employee_calendar_register.model';
import { CreateVacationsDialogComponent } from '../../../personal/my-absences/create-vacations-dialog/create-vacations-dialog.component';
import { resolveUserProfilePictureUrl } from 'src/app/components/utils/user-profile-picture/user-profile-picture.component';
import { UserService } from 'src/app/services/user.service';
import { EmployeeService } from 'src/app/services/employee.service';

type AbsenceUser = {
  name: string;
  surnames: string;
  pokemon_profile_picture_url?: string | null;
  serious_profile_picture_url?: string | null;
};

type AbsenceRegister = {
  id: number | string;
  from_date: Date | string;
  to_date: Date | string;
  all_day: boolean;
  user: AbsenceUser | undefined;
};

type Holiday = {
  id?: number | string;
  slug: string;
  name: string;
  username: string;
  surnames?: string | null;
  date: Date | string;
};

type GroupedHoliday = {
  id?: number | string;
  slug: string;
  name: string;
  users: string;
  from_date: Date | string;
  to_date: Date | string;
  all_day: boolean;
};

@Component({
  selector: 'app-vacations',
  templateUrl: './vacations.component.html',
  styleUrls: ['./vacations.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CalendarPreviousViewDirective,
    CalendarNextViewDirective,
    CalendarTodayDirective,
    CalendarDatePipe,
    CalendarMonthViewComponent,
    NgClass,
    MatTooltip,
    DatePipe,
    DecimalPipe,
    MatButton,
    MatIconButton,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    MatIcon,
    FormsModule,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
  ],
})
export class VacationsComponent implements OnInit {
  events: CalendarEvent[] = [];
  viewDate: Date = new Date();

  locale: string = 'es';
  weekStartsOn: number = DAYS_OF_WEEK.MONDAY;
  weekendDays: number[] = [DAYS_OF_WEEK.SATURDAY, DAYS_OF_WEEK.SUNDAY];
  refresh = new Subject<void>();
  view: CalendarView = CalendarView.Month;

  private api = inject(ApiService);
  private readonly permissions = inject(PermissionsService);
  private readonly dialog = inject(MatDialog);
  private readonly snack = inject(MatSnackBar);
  private readonly destroy_ref = inject(DestroyRef);
  private readonly user_service = inject(UserService);
  private readonly employee_service = inject(EmployeeService);

  /** When false (e.g. dashboard embed), hide admin worker-vacation management UI. */
  readonly show_admin_management = input(true);

  readonly isAdmin = this.permissions.isAdmin();
  readonly show_admin_vacation_management = computed(() => this.isAdmin && this.show_admin_management());
  readonly workers = signal<User[]>([]);
  selectedWorkerId: number | null = null;
  selectedYear = dayjs().year();
  years = [dayjs().subtract(1, 'year').year(), dayjs().year(), dayjs().add(1, 'year').year()];
  workerVacationsDataSource = new MatTableDataSource<EmployeeCalendarRegister>([]);
  workerVacationsColumns = ['date', 'status', 'year_imputed', 'actions'];
  workerSpentDays: number | null = null;

  ngOnInit(): void {
    this.fetchVacations();
    this.fetchHolidays();
    this.fetchBirthdays();
    this.fetchAllDayAndNoWorkingTimeAbsences();

    if (this.show_admin_vacation_management()) {
      this.loadWorkers();
    }
  }

  onViewDateChange() {
    this.events = [];
    this.fetchVacations();
    this.fetchHolidays();
    this.fetchBirthdays();
    this.fetchAllDayAndNoWorkingTimeAbsences();
  }

  onWorkerOrYearChange(): void {
    this.fetchSelectedWorkerVacations();
  }

  openCreateVacationsDialog(): void {
    if (!this.show_admin_vacation_management() || this.selectedWorkerId == null) {
      return;
    }

    this.dialog
      .open(CreateVacationsDialogComponent, {
        width: '1400px',
        data: { manageForOthers: true, userId: this.selectedWorkerId },
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe((result: { refresh?: boolean } | undefined): void => {
        if (result?.refresh === true) {
          this.onViewDateChange();
          this.fetchSelectedWorkerVacations();
        }
      });
  }

  isVacationCompleted(vacation: EmployeeCalendarRegister): boolean {
    return dayjs(vacation.from_date).isBefore(dayjs(), 'day');
  }

  deleteVacation(vacation: EmployeeCalendarRegister): void {
    if (!confirm('¿Estás seguro que quieres eliminar esta vacación?')) {
      return;
    }

    this.api
      .deleteVacation(vacation.id)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (): void => {
          this.snack.open('Vacación eliminada correctamente', 'Ok', { duration: 3000 });
          this.onViewDateChange();
          this.fetchSelectedWorkerVacations();
        },
      });
  }

  private loadWorkers(): void {
    this.api
      .getUsers({
        scopes: 'activeWorkers',
        order_by: 'name:asc',
        per_page: 500,
      })
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: response => {
          this.workers.set(response.data ?? []);
          if (this.selectedWorkerId == null && (response.data?.length ?? 0) > 0) {
            this.selectedWorkerId = response.data![0].id;
            this.fetchSelectedWorkerVacations();
          }
        },
      });
  }

  private fetchSelectedWorkerVacations(): void {
    if (!this.show_admin_vacation_management() || this.selectedWorkerId == null) {
      return;
    }

    this.api
      .getUserVacations(this.selectedWorkerId, this.selectedYear)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: data => {
          this.workerVacationsDataSource.data = data.registers ?? [];
          this.workerSpentDays = data.spent?.days ?? null;
        },
      });
  }

  private fetchAllDayAndNoWorkingTimeAbsences() {
    const from = dayjs(this.viewDate).startOf('month').toDate();
    const to = dayjs(this.viewDate).endOf('month').toDate();
    this.api
      .getAllDayAndNoWorkingTimeAbsencesFromWorkers(from, to)
      .subscribe((data: { employee_calendar_registers: AbsenceRegister[] }) => {
        const absencesByDate = new Map<string, Set<string>>();

        data.employee_calendar_registers.forEach((item: AbsenceRegister) => {
          const absenceFrom = dayjs(item.from_date).startOf('day');
          const absenceTo = dayjs(item.to_date).startOf('day');
          const userName = item.user!.name + ' ' + item.user!.surnames;

          for (let current = absenceFrom; current.isSameOrBefore(absenceTo); current = current.add(1, 'day')) {
            const dateKey = current.format('YYYY-MM-DD');
            if (!absencesByDate.has(dateKey)) {
              absencesByDate.set(dateKey, new Set<string>());
            }
            absencesByDate.get(dateKey)!.add(userName);
          }
        });

        this.events.push(
          ...Array.from(absencesByDate.entries()).map(([dateKey, usersSet]) => {
            const users = Array.from(usersSet);
            const primaryUser = users[0] ?? 'absence';

            const date: Date = dayjs(dateKey).toDate();

            return {
              title: users.join(', '),
              color: {
                primary: this.stringToColour(primaryUser),
                secondary: '#FAE3E3',
              },
              start: date,
              end: date,
              allDay: true,
              meta: {
                id: `absence-${dateKey}`,
                type: 'absence',
                absentUsers: users,
              },
            } as CalendarEvent;
          })
        );
        this.refresh.next();
      });
  }

  private fetchVacations() {
    const from = dayjs(this.viewDate).startOf('month').toDate();
    const to = dayjs(this.viewDate).endOf('month').toDate();
    this.api.getVacations(from, to).subscribe(data => {
      this.events.push(
        ...data.map(item => {
          return {
            title: item.user!.name + ' ' + item.user!.surnames,
            color: {
              primary: this.stringToColour(item.user!.name + ' ' + item.user!.surnames),
              secondary: '#FAE3E3',
            },
            start: item.from_date,
            end: item.to_date,
            allDay: item.all_day,
            meta: {
              id: item.id,
              imageUrl: resolveUserProfilePictureUrl(
                {
                  pokemon_profile_picture_url: item.user!.pokemon_profile_picture_url ?? 'assets/img/default_profile_picture.png',
                  serious_profile_picture_url: item.user!.serious_profile_picture_url ?? 'assets/img/default_profile_picture.png',
                },
                this.user_service.getCurrentUser()?.role?.slug,
                this.employee_service.isMeetingOngoing()
              ),
              type: 'vacation',
            },
          };
        })
      );
      this.refresh.next();
    });
  }

  private stringToColour(str: string): string {
    let hash = 0;

    str = str + 'ivitheboss';

    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }

    let colour = '#';
    for (let i = 0; i < 6; i++) {
      const value = (hash >> (i * 4)) & 0xf;
      colour += value.toString(16).padStart(1, '0');
    }

    if (colour.length === 4) {
      colour = '#' + colour[1] + colour[1] + colour[2] + colour[2] + colour[3] + colour[3];
    }

    return colour;
  }

  private fetchHolidays() {
    const from = dayjs(this.viewDate).startOf('month').toDate();
    const to = dayjs(this.viewDate).endOf('month').toDate();
    this.api.getHolidaysFromActiveWorkingContracts(from, to).subscribe((data: Holiday[]) => {
      const grouped: GroupedHoliday[] = [];

      data.forEach(item => {
        const index = grouped.findIndex(i => i.slug == item.slug);
        if (index == -1) {
          grouped.push({
            name: item.name,
            slug: item.slug,
            users: item.username + (item.surnames ? ' ' + item.surnames : ''),
            from_date: item.date,
            to_date: item.date,
            all_day: true,
          });
        } else {
          grouped[index].users += ', ' + item.username + (item.surnames ? ' ' + item.surnames : '');
        }
      });

      this.events.push(
        ...grouped.map(item => {
          return {
            title: item.name + ' (' + item.users + ')',
            color: {
              primary: '#FFD700',
              secondary: '#FAE3E3',
            },
            start: dayjs(item.from_date).toDate(),
            end: dayjs(item.to_date).toDate(),
            allDay: true,
            meta: {
              id: item.id,
              type: 'holiday',
            },
          };
        })
      );

      this.refresh.next();
    });
  }

  private fetchBirthdays() {
    const from = dayjs(this.viewDate).startOf('month').toDate();
    const to = dayjs(this.viewDate).endOf('month').toDate();
    this.api.getBirthdays(from, to).subscribe(data => {
      const birthdaysByDate = new Map<string, string[]>();

      data.forEach(item => {
        const dateKey = dayjs(item.day).format('YYYY-MM-DD');
        const fullName = item.user.name + ' ' + item.user.surnames + ' (+' + item.age + ')';

        if (!birthdaysByDate.has(dateKey)) {
          birthdaysByDate.set(dateKey, []);
        }
        birthdaysByDate.get(dateKey)!.push(fullName);
      });

      this.events.push(
        ...Array.from(birthdaysByDate.entries()).map(([dateKey, names]) => {
          const date: Date = dayjs(dateKey).toDate();

          return {
            title: names.join(', '),
            color: {
              primary: '#FF4500',
              secondary: '#FAE3E3',
            },
            start: date,
            end: date,
            allDay: true,
            meta: {
              id: `birthday-${dateKey}`,
              type: 'birthday',
            },
          };
        })
      );
      this.refresh.next();
    });
  }
}
